import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';

const HeaderContainer = styled.header`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 70px;
  
  @media (max-width: 768px) {
    padding: 0 15px;
    height: 60px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
  }
`;

const AtomLogo = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  position: relative;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  box-shadow: 0 4px 15px rgba(26, 26, 46, 0.4);
  overflow: hidden;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 25px rgba(26, 26, 46, 0.6);
    background: linear-gradient(135deg, #0f3460 0%, #16213e 50%, #1a1a2e 100%);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  /* Nucleus - hạt nhân màu vàng */
  &::before {
    content: '';
    position: absolute;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: radial-gradient(circle, #ffd700 0%, #ffb347 100%);
    box-shadow: 
      0 0 6px rgba(255, 215, 0, 0.8),
      0 0 12px rgba(255, 215, 0, 0.4);
    z-index: 4;
    animation: nucleusGlow 3s ease-in-out infinite alternate;
  }
  
  @keyframes nucleusGlow {
    0% {
      box-shadow: 
        0 0 6px rgba(255, 215, 0, 0.8),
        0 0 12px rgba(255, 215, 0, 0.4);
    }
    100% {
      box-shadow: 
        0 0 10px rgba(255, 215, 0, 1),
        0 0 20px rgba(255, 215, 0, 0.6);
    }
  }
  
  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    
    &::before {
      width: 5px;
      height: 5px;
    }
  }
`;

const AtomOrbit = styled.div<{ $color: string; $size: number; $duration: number; $direction?: string }>`
  position: absolute;
  width: ${props => props.$size}px;
  height: ${props => props.$size * 0.6}px; /* Elliptical orbit */
  border: 1.5px solid ${props => props.$color};
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: ${props => props.$direction === 'reverse' ? 'orbitReverse' : 'orbit'} ${props => props.$duration}s linear infinite;
  opacity: 0.8;
  
  /* Electron particle */
  &::before {
    content: '';
    position: absolute;
    width: 3px;
    height: 3px;
    background: ${props => props.$color};
    border-radius: 50%;
    top: -1.5px;
    left: 50%;
    transform: translateX(-50%);
    box-shadow: 0 0 4px ${props => props.$color};
    animation: ${props => props.$direction === 'reverse' ? 'electronReverse' : 'electron'} ${props => props.$duration}s linear infinite;
  }
  
  @keyframes orbit {
    0% { transform: translate(-50%, -50%) rotateZ(0deg); }
    100% { transform: translate(-50%, -50%) rotateZ(360deg); }
  }
  
  @keyframes orbitReverse {
    0% { transform: translate(-50%, -50%) rotateZ(360deg); }
    100% { transform: translate(-50%, -50%) rotateZ(0deg); }
  }
  
  @keyframes electron {
    0% { transform: translateX(-50%) rotateZ(0deg); }
    100% { transform: translateX(-50%) rotateZ(-360deg); }
  }
  
  @keyframes electronReverse {
    0% { transform: translateX(-50%) rotateZ(-360deg); }
    100% { transform: translateX(-50%) rotateZ(0deg); }
  }
`;

const BrandText = styled.div`
  display: flex;
  align-items: center;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-weight: 800;
  font-size: 1.5rem;
  letter-spacing: -0.5px;
  
  .infonium {
    color: white;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
  
  .org {
    color: #e53e3e;
    margin-left: 2px;
    text-shadow: 0 2px 4px rgba(229, 62, 62, 0.3);
  }
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const Tagline = styled.p`
  font-size: 0.75rem;
  margin: 0;
  opacity: 0.9;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.8);
  margin-top: -2px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const Navigation = styled.nav<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 30px;
  
  @media (max-width: 768px) {
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    backdrop-filter: blur(20px);
    flex-direction: column;
    padding: 20px;
    gap: 20px;
    transform: translateY(${props => props.isOpen ? '0' : '-100%'});
    transition: transform 0.3s ease;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  }
`;

const NavLink = styled.button<{ active?: boolean }>`
  background: none;
  border: none;
  color: white;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 8px;
  transition: all 0.2s ease;
  text-decoration: none;
  position: relative;
  
  ${props => props.active && `
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
  `}
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
    padding: 12px 20px;
    font-size: 18px;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogoClick = () => {
    navigate('/');
    setMobileMenuOpen(false);
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <HeaderContainer>
      <HeaderContent>
        <LogoContainer onClick={handleLogoClick}>
          <AtomLogo>
            {/* Quỹ đạo đỏ - Nhiệt huyết, năng lượng */}
            <AtomOrbit 
              $color="#e53e3e" 
              $size={28} 
              $duration={4}
            />
            {/* Quỹ đạo xanh lục - Sự sống, công nghệ sinh học */}
            <AtomOrbit 
              $color="#38a169" 
              $size={22} 
              $duration={3.5}
              $direction="reverse"
            />
            {/* Quỹ đạo xanh dương - Công nghệ, logic, dữ liệu */}
            <AtomOrbit 
              $color="#3182ce" 
              $size={16} 
              $duration={3}
            />
          </AtomLogo>
          <div>
            <BrandText>
              <span className="infonium">INFONIUM</span>
              <span className="org">.ORG</span>
            </BrandText>
            <Tagline>Science - Technology - Future</Tagline>
          </div>
        </LogoContainer>

        <Navigation isOpen={mobileMenuOpen}>
          <NavLink 
            active={isActive('/')} 
            onClick={() => handleNavClick('/')}
          >
            Home
          </NavLink>
          <NavLink 
            active={isActive('/about')} 
            onClick={() => handleNavClick('/about')}
          >
            About
          </NavLink>
          <NavLink 
            active={isActive('/privacy')} 
            onClick={() => handleNavClick('/privacy')}
          >
            Privacy
          </NavLink>
          <NavLink 
            active={isActive('/contact')} 
            onClick={() => handleNavClick('/contact')}
          >
            Contact
          </NavLink>
        </Navigation>

        <MobileMenuButton onClick={toggleMobileMenu}>
          {mobileMenuOpen ? '✕' : '☰'}
        </MobileMenuButton>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header; 